import { useState, useCallback } from 'react';
import type { ShapeKind } from '@/features/canvas/engine/nodes/shape/shapes';
import type { GifData } from '@/features/canvas/engine/nodes/gif/types';

export type ActiveTool =
  | { kind: 'select' }
  | { kind: 'hand' }
  | { kind: 'sticky'; stickyColor: string }
  | { kind: 'text' }
  | { kind: 'freedraw'; strokeColor: string; strokeWidth: number }
  | { kind: 'highlighter'; strokeColor: string; strokeWidth: number }
  | { kind: 'eraser' }
  | { kind: 'frame'; size: { width: number; height: number } | null }
  | { kind: 'shape'; shapeKind: ShapeKind }
  | { kind: 'connector' }
  | { kind: 'emoji'; ref: import('@/components/emoji-renderer/types').EmojiRef }
  | { kind: 'gif'; data: GifData };

export type ToolKind = ActiveTool['kind'];

const SELECT: ActiveTool = { kind: 'select' };

function toolsEqual(a: ActiveTool, b: ActiveTool): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'shape' && b.kind === 'shape') return a.shapeKind === b.shapeKind;
  if (a.kind === 'frame' && b.kind === 'frame') {
    const aw = a.size?.width ?? null;
    const bw = b.size?.width ?? null;
    const ah = a.size?.height ?? null;
    const bh = b.size?.height ?? null;
    return aw === bw && ah === bh;
  }
  return true;
}

export function useCanvasTool() {
  const [activeTool, setActiveTool] = useState<ActiveTool>(SELECT);

  const selectTool = useCallback((tool: ActiveTool) => {
    // freedraw/highlighter always set — changing color/size should never toggle off
    if (tool.kind === 'freedraw' || tool.kind === 'highlighter') {
      setActiveTool(tool);
      return;
    }
    setActiveTool(prev => toolsEqual(prev, tool) ? SELECT : tool);
  }, []);

  const reset = useCallback(() => setActiveTool(SELECT), []);

  const isActive = useCallback((kind: ToolKind) => activeTool.kind === kind, [activeTool]);

  return { activeTool, selectTool, reset, isActive };
}
